import { Box } from "@mui/material";
import { SecondaryTabs } from "@asayinc/component-library";
import { useState, useEffect, useMemo } from "react";
import { SearchJobNumbers } from "src/store/globalSearch/types";
import {
  SearchProxyEventResults,
  SearchReportResults,
  SearchBankruptcyResults,
  SearchClassActionResults,
} from "src/components/molecules/SearchEvents";

interface JobSearchResultsProps {
  jobResults: SearchJobNumbers | undefined;
  onClose: () => void;
  isTabulator?: boolean;
  isSolicitor?: boolean;
  isClaimsAdministrator?: boolean;
}

type TabValue = "Proxy" | "Report" | "Bankruptcy" | "Class Action";

export function JobSearchResults({
  jobResults,
  onClose,
  isTabulator = false,
  isSolicitor = false,
  isClaimsAdministrator = false,
}: JobSearchResultsProps) {
  const [activeTab, setActiveTab] = useState<TabValue>("Proxy");

  // Determine which tabs should be visible based on user roles
  // Tabulator: only proxy
  // Solicitor: proxy, reports (nobo, SRA, LAE)
  // Claims Administrator: bankruptcy, class action
  const showProxy = isTabulator || isSolicitor;
  const showReports = isSolicitor;
  const showBankruptcy = isClaimsAdministrator;
  const showClassAction = isClaimsAdministrator;

  const proxyCount = showProxy ? jobResults?.proxyEvents?.length ?? 0 : 0;
  const reportCount = showReports
    ? (jobResults?.noboEvents?.length ?? 0) +
      (jobResults?.shareRangeAnalysisEvents?.length ?? 0) +
      (jobResults?.locationAnalysisEvents?.length ?? 0)
    : 0;
  const bankruptcyCount = showBankruptcy
    ? jobResults?.bankruptcyEvents?.length ?? 0
    : 0;
  const classActionCount = showClassAction
    ? jobResults?.classActionEvents?.length ?? 0
    : 0;

  const allReports = useMemo(() => {
    if (!showReports) return [];
    return [
      ...(jobResults?.noboEvents ?? []),
      ...(jobResults?.shareRangeAnalysisEvents ?? []),
      ...(jobResults?.locationAnalysisEvents ?? []),
    ];
  }, [jobResults, showReports]);

  const totalResults =
    proxyCount + reportCount + bankruptcyCount + classActionCount;

  // Build tabs array based on what the user can see
  const tabs = useMemo(() => {
    const result = [];
    if (showProxy) {
      result.push({
        label: `${proxyCount} ${proxyCount === 1 ? "Proxy" : "Proxies"}`,
        value: "Proxy",
        disabled: proxyCount === 0,
        size: "small" as const,
      });
    }
    if (showReports) {
      result.push({
        label: `${reportCount} ${reportCount === 1 ? "Report" : "Reports"}`,
        value: "Report",
        disabled: reportCount === 0,
        size: "small" as const,
      });
    }
    if (showBankruptcy) {
      result.push({
        label: `${bankruptcyCount} ${
          bankruptcyCount === 1 ? "Bankruptcy" : "Bankruptcies"
        }`,
        value: "Bankruptcy",
        disabled: bankruptcyCount === 0,
        size: "small" as const,
      });
    }
    if (showClassAction) {
      result.push({
        label: `${classActionCount} ${
          classActionCount === 1 ? "Class Action" : "Class Actions"
        }`,
        value: "Class Action",
        disabled: classActionCount === 0,
        size: "small" as const,
      });
    }
    return result;
  }, [
    showProxy,
    showReports,
    showBankruptcy,
    showClassAction,
    proxyCount,
    reportCount,
    bankruptcyCount,
    classActionCount,
  ]);

  useEffect(() => {
    // Set initial active tab based on what's available and visible
    if (showProxy && proxyCount > 0) setActiveTab("Proxy");
    else if (showReports && reportCount > 0) setActiveTab("Report");
    else if (showBankruptcy && bankruptcyCount > 0) setActiveTab("Bankruptcy");
    else if (showClassAction && classActionCount > 0)
      setActiveTab("Class Action");
    else if (tabs.length > 0) setActiveTab(tabs[0].value as TabValue);
  }, [
    proxyCount,
    reportCount,
    bankruptcyCount,
    classActionCount,
    showProxy,
    showReports,
    showBankruptcy,
    showClassAction,
    tabs,
  ]);

  if (!jobResults || totalResults === 0 || tabs.length === 0) return null;

  return (
    <Box sx={{ mt: 6 }}>
      <Box display="flex" justifyContent="center" mb={4}>
        <SecondaryTabs
          tabs={tabs}
          activeTab={activeTab}
          clickHandler={(tab) => setActiveTab(tab as TabValue)}
          sx={{ width: "fit-content" }}
        />
      </Box>

      {activeTab === "Proxy" && showProxy && (
        <SearchProxyEventResults
          proxyEvents={jobResults.proxyEvents ?? []}
          closeSearchDrawer={onClose}
        />
      )}
      {activeTab === "Report" && showReports && (
        <SearchReportResults reports={allReports} closeSearchDrawer={onClose} />
      )}
      {activeTab === "Bankruptcy" && showBankruptcy && (
        <SearchBankruptcyResults
          bankruptcyEvents={jobResults.bankruptcyEvents ?? []}
          closeSearchDrawer={onClose}
        />
      )}
      {activeTab === "Class Action" && showClassAction && (
        <SearchClassActionResults
          classActionEvents={jobResults.classActionEvents ?? []}
          closeSearchDrawer={onClose}
        />
      )}
    </Box>
  );
}
